import { type DrillProgress, isFinished } from "./xiezi-stages.js";

/**
 * The moments in a writing drill that make a sound, and what each sound file
 * is called.
 *
 * The one thing the asset build and the browser bundle have to agree about.
 * `src/assets/feedback-sounds.ts` copies a `uisfx` cue to each of these names,
 * and `src/site/xiezi-sounds.ts` plays them. They are published beside the
 * three 复习 notes, under the same base, and named after the moment they mark
 * rather than the cue they came from, for the reason
 * `src/user/fuxi-sound-addresses.ts` gives.
 *
 * Hugh chose the five cues on 2026-10-15, out of the Minimal pack 复习 uses:
 *
 * - `stroke` is a stroke taken, which is `hover`, the quietest cue in the
 *   pack. A character is written a stroke at a time, sixty strokes a minute,
 *   and anything louder would be a metronome.
 * - `miss` is a stroke not taken, or a character from memory marked wrong,
 *   which is `invalid-drop`.
 * - `written` is a pass finished, the whole character written, which is
 *   `checkpoint`. The character is said straight after it.
 * - `stage` is a stage finished, which is `level-up`.
 * - `drill` is the whole drill finished, which is `achievement`.
 */

/** The five moments, from the most frequent to the least. */
export const drillMoments = [
  "stroke",
  "miss",
  "written",
  "stage",
  "drill",
] as const;

/** One of the five moments. */
export type DrillMoment = (typeof drillMoments)[number];

/**
 * What one moment's sound is published as.
 * @param moment - The moment.
 * @returns The file name, without a directory.
 */
export function drillSoundFile(moment: DrillMoment): string {
  return `xiezi-${moment}.mp3`;
}

/**
 * The moment a finished pass is heard as.
 *
 * A pass that finishes the last stage is the whole drill done, unless other
 * characters of an Exercise are still to finish, when it is heard as a stage.
 * One that moves the reader to the next stage is a stage, and any other is the
 * character written.
 *
 * @param before - Where the reader was before the pass.
 * @param after - Where the pass left them.
 * @param finishesDrill - Whether finishing this character finishes the drill.
 * @returns The moment.
 */
export function passMoment(
  before: DrillProgress,
  after: DrillProgress,
  finishesDrill: boolean,
): DrillMoment {
  if (isFinished(after)) {
    return finishesDrill ? "drill" : "stage";
  }

  return after.stage > before.stage ? "stage" : "written";
}
